import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSpring } from 'react-spring';
import { Keys } from 'react-keydown';

import Button, { ButtonGroup } from 'components/Button';
import Card from 'components/Card';
import TextHeader from 'components/TextHeader';

import useWindowDimensions from 'hooks/useWindowDimensions';
import useBreakpoint from 'hooks/useBreakpoint';
import useOnKeyPress from 'hooks/useOnKeyPress';
import { displayAlertError, generateSelectorsFromTestIds } from 'helpers';

import {
  CARD_MARGIN,
  CARD_WIDTH,
  Wrapper,
  TourWrapper,
  CardWrapper,
  StyledContent,
  DescriptionContainer,
  ButtonWrapper,
  StepLabel,
  Background
} from './styled';

const Tour = ({ backgroundImageSrc, steps, onFinish, onFinishText = 'Finish' }) => {
  const [tourIndex, setTourIndex] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);

  const isSmallScreen = useBreakpoint(
    useBreakpoint.BREAKPOINTS.SMALL,
    useBreakpoint.DIRECTIONS.DOWN
  );
  const { width: windowWidth } = useWindowDimensions();

  const xOffset = isSmallScreen
    ? tourIndex * windowWidth
    : tourIndex * (CARD_WIDTH + CARD_MARGIN * 2);

  const wrapperStyleProps = useSpring({
    transform: `translate3d(-${xOffset}px, 0, 0)`
  });

  const decrementStep = () => {
    if (isDisabled || tourIndex === 0) return;

    setTourIndex(tourIndex - 1);
  };

  const incrementStep = () => {
    if (isDisabled || tourIndex === steps.length - 1) return;

    setTourIndex(tourIndex + 1);
  };

  const getButtonText = index => {
    if (index === 0) return 'Get Started';
    if (index === steps.length - 1) return onFinishText;

    return 'Next Tip';
  };

  const getButtonAction = index => {
    if (index === steps.length - 1) {
      setIsDisabled(true);
      return onFinish()
        .catch(displayAlertError)
        .finally(() => setIsDisabled(false));
    }
    return incrementStep();
  };

  useOnKeyPress(Keys.RIGHT, incrementStep);
  useOnKeyPress(Keys.LEFT, decrementStep);

  return (
    <Wrapper>
      <TourWrapper style={wrapperStyleProps} pagewidth={windowWidth}>
        <div data-testid={Tour.TEST_IDS.getTestIdForActiveCard(tourIndex)} />

        {steps.map(({ title, description }, index) => (
          <CardWrapper
            $active={index === tourIndex}
            index={title}
            key={title}
            disabled={isDisabled}
            onClick={() => index !== tourIndex && setTourIndex(index)}
            data-testid={Tour.TEST_IDS.CARD}
          >
            <Card data-testid={Tour.TEST_IDS.getTestIdForCard(index)}>
              <StyledContent>
                <Card.Title>{title}</Card.Title>
                <DescriptionContainer>
                  <TextHeader
                    type={TextHeader.TYPES.MEDIUM}
                    weight={TextHeader.WEIGHTS.LIGHT}
                  >
                    {description}
                  </TextHeader>
                </DescriptionContainer>

                <ButtonWrapper>
                  <ButtonGroup className="mb-0">
                    {index !== 0 && (
                      <Button
                        disabled={isDisabled}
                        color={Button.COLORS.TRANSPARENT}
                        onClick={() => decrementStep()}
                        data-testid={Tour.TEST_IDS.PREV_CARD_BUTTON}
                      >
                        Previous Tip
                      </Button>
                    )}

                    <Button
                      disabled={isDisabled}
                      color={Button.COLORS.PRIMARY}
                      onClick={() => getButtonAction(index)}
                      data-testid={Tour.TEST_IDS.NEXT_CARD_BUTTON}
                    >
                      {getButtonText(index)}
                    </Button>
                  </ButtonGroup>
                </ButtonWrapper>
              </StyledContent>
            </Card>
            <StepLabel>
              {index + 1} of {steps.length}
            </StepLabel>
          </CardWrapper>
        ))}
      </TourWrapper>
      <Background imageSrc={backgroundImageSrc} />
    </Wrapper>
  );
};

Tour.propTypes = {
  backgroundImageSrc: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired
    })
  ).isRequired,
  onFinish: PropTypes.func.isRequired,
  onFinishText: PropTypes.string
};

Tour.TEST_IDS = {
  CARD: 'Tour-card',
  NEXT_CARD_BUTTON: 'Tour-next-card-button',
  PREV_CARD_BUTTON: 'Tour-prev-card-button',
  getTestIdForActiveCard: index => `Tour-active-card-index-${index}`,
  getTestIdForCard: index => `Tour-card-index-${index}`
};

Tour.SELECTORS = generateSelectorsFromTestIds(Tour.TEST_IDS);

export default Tour;
